import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
    Store,
    Search,
    Bookmark,
    BookmarkCheck,
    Star,
    User,
    BarChart2,
    Plus,
    Trash2,
    Pencil,
    Megaphone,
    Mail,
    Palette,
    FileText,
    Users,
    ChevronDown,
    Eye,
    X,
    Monitor,
    Globe,
} from 'lucide-react';
import {
    GlassCard,
    GlassButton,
    GlassInput,
    GlassSelect,
    GlassTabs,
    GlassModal,
    ModuleHeader,
} from '../ui';
import {
    getMarketplaceTemplates,
    getFeaturedTemplates,
    getMyMarketplaceTemplates,
    getSavedTemplates,
    publishTemplate,
    deleteMarketplaceTemplate,
    saveMarketplaceTemplate,
    useMarketplaceTemplate as applyMarketplaceTemplate,
    getBrandGuides,
    getCampaigns,
    MarketplaceTemplate,
    Campaign,
} from '../../services/api';
import './Marketplace.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContentContextType {
    selectedProjectId?: string;
    onProjectSelect?: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Category config
// ---------------------------------------------------------------------------

const CATEGORIES = [
    { value: '', label: 'All', icon: Store },
    { value: 'site_builder', label: 'Sites', icon: Monitor },
    { value: 'landing_page', label: 'Landing Pages', icon: Globe },
    { value: 'campaign', label: 'Campaigns', icon: Megaphone },
    { value: 'email_sequence', label: 'Email Sequences', icon: Mail },
    { value: 'lead_form', label: 'Lead Forms', icon: FileText },
    { value: 'brand_guide', label: 'Brand Guides', icon: Palette },
    { value: 'content', label: 'Content', icon: Users },
];

const INDUSTRY_FILTERS = [
    { label: 'All', value: '' },
    { label: 'Legal', value: 'legal' },
    { label: 'Financial', value: 'financial' },
    { label: 'Real Estate', value: 'real-estate' },
    { label: 'Healthcare', value: 'healthcare' },
    { label: 'Technology', value: 'technology' },
    { label: 'SaaS', value: 'saas' },
    { label: 'Retail', value: 'retail' },
    { label: 'Consulting', value: 'consulting' },
    { label: 'Hospitality', value: 'hospitality' },
    { label: 'Fitness', value: 'fitness' },
    { label: 'Education', value: 'education' },
    { label: 'Ecommerce', value: 'ecommerce' },
    { label: 'Beauty', value: 'beauty' },
    { label: 'Trades', value: 'trades' },
    { label: 'Automotive', value: 'automotive' },
    { label: 'Non-Profit', value: 'non-profit' },
    { label: 'Marketing', value: 'marketing' },
];

const CATEGORY_LABELS: Record<string, string> = {
    campaign: 'Campaign',
    email_sequence: 'Email Sequence',
    brand_guide: 'Brand Guide',
    lead_form: 'Lead Form',
    landing_page: 'Landing Pages',
    content: 'Content',
};

const CATEGORY_OPTIONS = [
    { value: 'campaign', label: 'Campaign' },
    { value: 'email_sequence', label: 'Email Sequence' },
    { value: 'brand_guide', label: 'Brand Guide' },
    { value: 'lead_form', label: 'Lead Form' },
    { value: 'content', label: 'Content' },
];

// ---------------------------------------------------------------------------
// Toast helper
// ---------------------------------------------------------------------------

interface Toast {
    id: number;
    message: string;
    type: 'success' | 'error' | 'info';
}

// ---------------------------------------------------------------------------
// Template card
// ---------------------------------------------------------------------------

interface TemplateCardProps {
    template: MarketplaceTemplate;
    onSave: (id: string) => void;
    onUse: (id: string) => void;
    onPreview: (template: MarketplaceTemplate) => void;
    onDelete?: (id: string) => void;
    onEdit?: (template: MarketplaceTemplate) => void;
    savingId: string | null;
    usingId: string | null;
    featured?: boolean;
    isOwner?: boolean;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
    template,
    onSave,
    onUse,
    onPreview,
    onDelete,
    onEdit,
    savingId,
    usingId,
    featured = false,
    isOwner = false,
}) => {
    const catClass = `cat-badge-${template.category}`;

    return (
        <GlassCard className={`template-card${featured ? ' featured-card' : ''}`}>
            {template.thumbnail_url && (
                <div style={{ margin: '-1px -1px 0', borderRadius: '12px 12px 0 0', overflow: 'hidden', aspectRatio: '16/9', background: 'var(--panel-strong, #f1f5f9)' }}>
                    <img
                        src={template.thumbnail_url}
                        alt={template.name}
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
                    />
                </div>
            )}

            <div className="template-card-header">
                <div className="template-card-badges">
                    <span className={`template-tag ${catClass}`} style={{ fontWeight: 600 }}>
                        {CATEGORY_LABELS[template.category] || template.category}
                    </span>
                    {template.featured && (
                        <span className="featured-star" title="Featured template">
                            <Star size={13} fill="currentColor" />
                        </span>
                    )}
                </div>
                {!isOwner && (
                    <GlassButton
                        variant="ghost"
                        onClick={() => onSave(template.template_id)}
                        disabled={savingId === template.template_id}
                        title={template.is_saved ? 'Unsave' : 'Save for later'}
                        style={{ padding: '4px 6px', flexShrink: 0 }}
                    >
                        {template.is_saved
                            ? <BookmarkCheck size={15} style={{ color: 'var(--primary)' }} />
                            : <Bookmark size={15} />
                        }
                    </GlassButton>
                )}
            </div>

            <h4 className="template-card-name">{template.name}</h4>

            {template.description && (
                <p className="template-card-desc">{template.description}</p>
            )}

            {template.tags && template.tags.length > 0 && (
                <div className="template-card-tags">
                    {template.tags.slice(0, 4).map(tag => (
                        <span key={tag} className="template-tag">{tag}</span>
                    ))}
                </div>
            )}

            <div className="template-card-meta">
                <span className="template-card-owner">
                    <User size={11} />
                    {template.owner_name || 'Community'}
                </span>
                <span className="template-card-uses">
                    <BarChart2 size={11} />
                    {template.use_count} {template.use_count === 1 ? 'use' : 'uses'}
                </span>
            </div>

            <div className="template-card-actions">
                {isOwner ? (
                    <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                        <GlassButton
                            variant="secondary"
                            onClick={() => onPreview(template)}
                            title="Preview template"
                            style={{ padding: '6px 10px' }}
                        >
                            <Eye size={14} />
                            Preview
                        </GlassButton>
                        <GlassButton
                            variant="secondary"
                            onClick={() => onEdit && onEdit(template)}
                            style={{ flex: 1 }}
                        >
                            <Pencil size={14} />
                            Edit
                        </GlassButton>
                        <GlassButton
                            variant="danger"
                            onClick={() => onDelete && onDelete(template.template_id)}
                        >
                            <Trash2 size={14} />
                        </GlassButton>
                    </div>
                ) : (
                    <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                        <GlassButton
                            variant="secondary"
                            onClick={() => onPreview(template)}
                            title="Preview template"
                            style={{ padding: '6px 10px' }}
                        >
                            <Eye size={14} />
                            Preview
                        </GlassButton>
                        <GlassButton
                            variant="primary"
                            onClick={() => onUse(template.template_id)}
                            disabled={usingId === template.template_id}
                            style={{ flex: 1 }}
                        >
                            {usingId === template.template_id ? 'Applying...' : 'Use Template'}
                        </GlassButton>
                    </div>
                )}
            </div>
        </GlassCard>
    );
};

// ---------------------------------------------------------------------------
// Main module
// ---------------------------------------------------------------------------

const TABS = [
    { id: 'browse', label: 'Browse' },
    { id: 'saved', label: 'Saved' },
    { id: 'mine', label: 'My Templates' },
];

const PAGE_SIZE = 24;

const MarketplaceModule: React.FC = () => {
    const navigate = useNavigate();
    const { selectedProjectId } = useOutletContext<ContentContextType>();

    // ---- Tabs & filters ----
    const [activeTab, setActiveTab] = useState('browse');
    const [category, setCategory] = useState('');
    const [industry, setIndustry] = useState('');
    const [search, setSearch] = useState('');

    // ---- Browse data ----
    const [featured, setFeatured] = useState<MarketplaceTemplate[]>([]);
    const [templates, setTemplates] = useState<MarketplaceTemplate[]>([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loadingBrowse, setLoadingBrowse] = useState(false);

    // ---- Saved data ----
    const [saved, setSaved] = useState<MarketplaceTemplate[]>([]);
    const [loadingSaved, setLoadingSaved] = useState(false);

    // ---- My templates data ----
    const [mine, setMine] = useState<MarketplaceTemplate[]>([]);
    const [loadingMine, setLoadingMine] = useState(false);

    // ---- Interaction state ----
    const [savingId, setSavingId] = useState<string | null>(null);
    const [usingId, setUsingId] = useState<string | null>(null);

    // ---- Preview modal ----
    const [previewTemplate, setPreviewTemplate] = useState<MarketplaceTemplate | null>(null);

    // ---- Publish modal ----
    const [publishOpen, setPublishOpen] = useState(false);
    const [editTemplate, setEditTemplate] = useState<MarketplaceTemplate | null>(null);
    const [publishForm, setPublishForm] = useState({
        name: '',
        category: 'campaign',
        description: '',
        tags: '',
        template_data: '{}',
        is_public: true,
    });
    const [publishLoading, setPublishLoading] = useState(false);

    // ---- Publish-from-campaign shortcut ----
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [publishFromCampaignId, setPublishFromCampaignId] = useState('');
    const [publishingFrom, setPublishingFrom] = useState(false);

    // ---- Toasts ----
    const [toasts, setToasts] = useState<Toast[]>([]);
    let toastId = 0;

    // ---- Landing Page Use modal ----
    const [lpModal, setLpModal] = useState<{ templateId: string; templateName: string; templateType: string } | null>(null);
    const [lpTitle, setLpTitle] = useState('');
    const [lpBrandGuideId, setLpBrandGuideId] = useState('');
    const [lpBrandGuides, setLpBrandGuides] = useState<Array<{ brand_guide_id: string; brand_name?: string; brand_guide_name?: string }>>([]);
    const [lpApplying, setLpApplying] = useState(false);

    const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
        const id = ++toastId;
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- Load featured ----
    const loadFeatured = useCallback(async () => {
        try {
            const data = await getFeaturedTemplates();
            setFeatured(data);
        } catch (e) {
            console.error('Failed to load featured templates:', e);
        }
    }, []);

    // ---- Load browse ----
    const loadBrowse = useCallback(async (reset = false) => {
        setLoadingBrowse(true);
        try {
            const nextOffset = reset ? 0 : offset;
            const result = await getMarketplaceTemplates({
                category: category || undefined,
                search: search || undefined,
                tags: (category === 'site_builder' && industry) ? industry : undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            if (reset) {
                setTemplates(result.templates);
                setOffset(0);
            } else {
                setTemplates(prev => [...prev, ...result.templates]);
            }
            setTotal(result.total);
        } catch (e) {
            console.error('Failed to load templates:', e);
        } finally {
            setLoadingBrowse(false);
        }
    }, [category, search, offset, industry]);

    // ---- Load saved ----
    const loadSaved = useCallback(async () => {
        setLoadingSaved(true);
        try {
            const data = await getSavedTemplates();
            setSaved(data);
        } catch (e) {
            console.error('Failed to load saved:', e);
        } finally {
            setLoadingSaved(false);
        }
    }, []);

    // ---- Load mine ----
    const loadMine = useCallback(async () => {
        setLoadingMine(true);
        try {
            const data = await getMyMarketplaceTemplates();
            setMine(data);
        } catch (e) {
            console.error('Failed to load my templates:', e);
        } finally {
            setLoadingMine(false);
        }
    }, []);

    // ---- Initial loads ----
    useEffect(() => {
        loadFeatured();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (activeTab === 'browse') {
            setOffset(0);
            loadBrowse(true);
        }
    }, [activeTab, category, search, industry]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (activeTab === 'saved') loadSaved();
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (activeTab === 'mine') {
            loadMine();
            // Load campaigns for "publish from" shortcut
            getCampaigns().then(setCampaigns).catch(() => {});
        }
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- Handlers ----
    const handleSave = async (templateId: string) => {
        setSavingId(templateId);
        try {
            const result = await saveMarketplaceTemplate(templateId);
            const isSaved = result.saved;
            // Update browse list
            setTemplates(prev => prev.map(t =>
                t.template_id === templateId ? { ...t, is_saved: isSaved } : t
            ));
            setFeatured(prev => prev.map(t =>
                t.template_id === templateId ? { ...t, is_saved: isSaved } : t
            ));
            if (!isSaved) {
                setSaved(prev => prev.filter(t => t.template_id !== templateId));
            }
            showToast(isSaved ? 'Template saved' : 'Template unsaved', 'info');
        } catch (e) {
            showToast('Failed to save template', 'error');
        } finally {
            setSavingId(null);
        }
    };

    const openLpModal = (template: MarketplaceTemplate) => {
        setLpTitle(template.name);
        setLpBrandGuideId('');
        setLpModal({
            templateId: template.template_id,
            templateName: template.name,
            templateType: template.template_data?.lp_template_type || 'lead_capture',
        });
        // Load brand guides for selected project
        if (selectedProjectId) {
            getBrandGuides(selectedProjectId)
                .then(guides => setLpBrandGuides(Array.isArray(guides) ? guides : []))
                .catch(() => setLpBrandGuides([]));
        }
    };

    const handleLpApply = async () => {
        if (!lpModal || !selectedProjectId) return;
        setLpApplying(true);
        try {
            const result = await applyMarketplaceTemplate(lpModal.templateId, selectedProjectId, {
                title: lpTitle,
                brandGuideId: lpBrandGuideId || undefined,
            });
            setLpModal(null);
            if (result.navigate_to) {
                navigate(result.navigate_to);
            }
        } catch (err: any) {
            showToast(getApiError(err, 'Failed to create landing page'), 'error');
        } finally {
            setLpApplying(false);
        }
    };

    const handleUse = async (templateId: string) => {
        const projectId = selectedProjectId;
        if (!projectId) {
            showToast('Please select a project first', 'error');
            return;
        }
        // Landing page templates use a dedicated modal for title + brand guide selection
        const allTemplates = [...templates, ...featured, ...saved, ...mine];
        const template = allTemplates.find(t => t.template_id === templateId);
        if (template?.category === 'landing_page') {
            openLpModal(template);
            return;
        }
        setUsingId(templateId);
        try {
            const result = await applyMarketplaceTemplate(templateId, projectId);
            showToast(result.message || 'Template applied successfully', 'success');
            if (result.navigate_to) {
                setTimeout(() => navigate(result.navigate_to), 800);
            }
            // Refresh template lists to get updated use_count
            loadBrowse(true);
        } catch (e: any) {
            showToast(getApiError(e, 'Failed to apply template'), 'error');
        } finally {
            setUsingId(null);
        }
    };

    const handleDeleteMine = async (templateId: string) => {
        if (!window.confirm('Delete this template from the marketplace?')) return;
        try {
            await deleteMarketplaceTemplate(templateId);
            setMine(prev => prev.filter(t => t.template_id !== templateId));
            showToast('Template deleted', 'info');
        } catch (e) {
            showToast('Failed to delete template', 'error');
        }
    };

    const handleEditMine = (template: MarketplaceTemplate) => {
        setEditTemplate(template);
        setPublishForm({
            name: template.name,
            category: template.category,
            description: template.description || '',
            tags: (template.tags || []).join(', '),
            template_data: JSON.stringify(template.template_data || {}, null, 2),
            is_public: template.is_public,
        });
        setPublishOpen(true);
    };

    const handleOpenPublish = () => {
        setEditTemplate(null);
        setPublishForm({
            name: '',
            category: 'campaign',
            description: '',
            tags: '',
            template_data: '{}',
            is_public: true,
        });
        setPublishOpen(true);
    };

    const handlePublishSubmit = async () => {
        if (!publishForm.name.trim()) {
            showToast('Name is required', 'error');
            return;
        }
        let parsedData: any = {};
        try {
            parsedData = JSON.parse(publishForm.template_data);
        } catch {
            showToast('Template data must be valid JSON', 'error');
            return;
        }

        setPublishLoading(true);
        try {
            const payload = {
                name: publishForm.name.trim(),
                category: publishForm.category,
                description: publishForm.description.trim() || undefined,
                tags: publishForm.tags
                    ? publishForm.tags.split(',').map((t: string) => t.trim()).filter(Boolean)
                    : [],
                template_data: parsedData,
                is_public: publishForm.is_public,
            };

            if (editTemplate) {
                // Update existing
                const { apiClient } = await import('../../services/api');
                await apiClient.patch(`/marketplace/templates/${editTemplate.template_id}`, payload);
                showToast('Template updated', 'success');
            } else {
                await publishTemplate(payload);
                showToast('Template published to marketplace', 'success');
            }

            setPublishOpen(false);
            loadMine();
        } catch (e: any) {
            showToast(getApiError(e, 'Failed to publish template'), 'error');
        } finally {
            setPublishLoading(false);
        }
    };

    const handlePublishFromCampaign = async () => {
        if (!publishFromCampaignId) return;
        setPublishingFrom(true);
        try {
            const { apiClient } = await import('../../services/api');
            await apiClient.post(`/marketplace/publish-from/campaign/${publishFromCampaignId}`);
            showToast('Campaign published as template', 'success');
            setPublishFromCampaignId('');
            loadMine();
        } catch (e: any) {
            showToast(getApiError(e, 'Failed to publish campaign'), 'error');
        } finally {
            setPublishingFrom(false);
        }
    };

    const handleLoadMore = () => {
        const nextOffset = offset + PAGE_SIZE;
        setOffset(nextOffset);
        // We need to load with the updated offset
        setLoadingBrowse(true);
        getMarketplaceTemplates({
            category: category || undefined,
            search: search || undefined,
            limit: PAGE_SIZE,
            offset: nextOffset,
        }).then(result => {
            setTemplates(prev => [...prev, ...result.templates]);
            setTotal(result.total);
        }).catch(e => {
            console.error('Load more failed:', e);
        }).finally(() => {
            setLoadingBrowse(false);
        });
    };

    const campaignOptions = campaigns.map(c => ({
        value: c.campaign_id,
        label: c.name,
    }));

    // ---- Render ----
    if (!selectedProjectId) return (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
            <Store size={64} style={{ opacity: 0.4, marginBottom: '16px' }} />
            <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>Select a project</h3>
            <p>Choose a project from the sidebar to view the marketplace</p>
        </div>
    );

    return (
        <div className="marketplace-page">
            <ModuleHeader
                title="Template Marketplace"
                actions={[]}
            />

            <GlassTabs
                tabs={TABS}
                activeTab={activeTab}
                onChange={setActiveTab}
            />

            {/* ---- Browse Tab ---- */}
            {activeTab === 'browse' && (
                <>
                    {/* Search */}
                    <div className="marketplace-filter-bar">
                        <GlassInput
                            placeholder="Search templates..."
                            value={search}
                            onChange={(v) => { setSearch(v); setOffset(0); }}
                            icon={<Search size={14} />}
                        />
                    </div>

                    {/* Category chips */}
                    <div className="category-chips">
                        {CATEGORIES.map(cat => {
                            const IconComp = cat.icon;
                            return (
                                <button
                                    key={cat.value}
                                    className={`category-chip${category === cat.value ? ' active' : ''}`}
                                    onClick={() => { setCategory(cat.value); setIndustry(''); setOffset(0); }}
                                >
                                    <IconComp size={13} />
                                    {cat.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Industry filter pills — only for Site Builder category */}
                    {category === 'site_builder' && (
                        <div className="category-chips">
                            {INDUSTRY_FILTERS.map(f => (
                                <button
                                    key={f.value}
                                    className={`category-chip${industry === f.value ? ' active' : ''}`}
                                    onClick={() => { setIndustry(f.value); setOffset(0); }}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Featured section */}
                    {featured.length > 0 && !search && !category && (
                        <div className="featured-section">
                            <h3>
                                <Star size={14} fill="currentColor" style={{ color: '#f59e0b' }} />
                                Featured Templates
                            </h3>
                            <div className="featured-scroll-row">
                                {featured.map(t => (
                                    <TemplateCard
                                        key={t.template_id}
                                        template={t}
                                        onSave={handleSave}
                                        onUse={handleUse}
                                        onPreview={setPreviewTemplate}
                                        savingId={savingId}
                                        usingId={usingId}
                                        featured
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Main grid */}
                    {loadingBrowse && templates.length === 0 ? (
                        <div className="marketplace-loading">
                            <div className="marketplace-spinner" />
                            Loading templates...
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="marketplace-empty">
                            <Store size={48} className="marketplace-empty-icon" />
                            <p style={{ margin: 0, fontWeight: 600, color: 'var(--text)' }}>No templates found</p>
                            <p style={{ margin: 0, fontSize: '0.87rem' }}>
                                {search || category ? 'Try a different search or category.' : 'The marketplace is empty.'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="templates-grid">
                                {templates.map(t => (
                                    <TemplateCard
                                        key={t.template_id}
                                        template={t}
                                        onSave={handleSave}
                                        onUse={handleUse}
                                        onPreview={setPreviewTemplate}
                                        savingId={savingId}
                                        usingId={usingId}
                                    />
                                ))}
                            </div>
                            {templates.length < total && (
                                <div className="load-more-row">
                                    <GlassButton
                                        variant="secondary"
                                        onClick={handleLoadMore}
                                        disabled={loadingBrowse}
                                    >
                                        <ChevronDown size={15} />
                                        {loadingBrowse ? 'Loading...' : `Load More (${total - templates.length} remaining)`}
                                    </GlassButton>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* ---- Saved Tab ---- */}
            {activeTab === 'saved' && (
                <>
                    {loadingSaved ? (
                        <div className="marketplace-loading">
                            <div className="marketplace-spinner" />
                            Loading saved templates...
                        </div>
                    ) : saved.length === 0 ? (
                        <div className="marketplace-empty">
                            <Bookmark size={48} className="marketplace-empty-icon" />
                            <p style={{ margin: 0, fontWeight: 600, color: 'var(--text)' }}>No saved templates yet</p>
                            <p style={{ margin: 0, fontSize: '0.87rem' }}>
                                Save templates you want to use later from the Browse tab.
                            </p>
                        </div>
                    ) : (
                        <div className="templates-grid">
                            {saved.map(t => (
                                <TemplateCard
                                    key={t.template_id}
                                    template={t}
                                    onSave={handleSave}
                                    onUse={handleUse}
                                    onPreview={setPreviewTemplate}
                                    savingId={savingId}
                                    usingId={usingId}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ---- My Templates Tab ---- */}
            {activeTab === 'mine' && (
                <>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                        <GlassButton variant="primary" onClick={handleOpenPublish}>
                            <Plus size={15} />
                            Publish Template
                        </GlassButton>

                        {/* Publish from campaign shortcut */}
                        {campaignOptions.length > 0 && (
                            <div className="publish-from-row">
                                <GlassSelect
                                    options={campaignOptions}
                                    value={publishFromCampaignId}
                                    onChange={setPublishFromCampaignId}
                                    placeholder="Publish from campaign..."
                                />
                                {publishFromCampaignId && (
                                    <GlassButton
                                        variant="secondary"
                                        onClick={handlePublishFromCampaign}
                                        disabled={publishingFrom}
                                    >
                                        {publishingFrom ? 'Publishing...' : 'Publish'}
                                    </GlassButton>
                                )}
                            </div>
                        )}
                    </div>

                    {loadingMine ? (
                        <div className="marketplace-loading">
                            <div className="marketplace-spinner" />
                            Loading your templates...
                        </div>
                    ) : mine.length === 0 ? (
                        <div className="marketplace-empty">
                            <Store size={48} className="marketplace-empty-icon" />
                            <p style={{ margin: 0, fontWeight: 600, color: 'var(--text)' }}>No published templates yet</p>
                            <p style={{ margin: 0, fontSize: '0.87rem' }}>
                                Share a template with the community using the "Publish Template" button above.
                            </p>
                        </div>
                    ) : (
                        <div className="templates-grid">
                            {mine.map(t => (
                                <TemplateCard
                                    key={t.template_id}
                                    template={t}
                                    onSave={handleSave}
                                    onUse={handleUse}
                                    onPreview={setPreviewTemplate}
                                    onDelete={handleDeleteMine}
                                    onEdit={handleEditMine}
                                    savingId={savingId}
                                    usingId={usingId}
                                    isOwner
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ---- Preview Modal ---- */}
            {previewTemplate && (
                <div className="mp-preview-overlay" onClick={() => setPreviewTemplate(null)}>
                    <div className="mp-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="mp-preview-header">
                            <div>
                                <span className="mp-preview-title">{previewTemplate.name}</span>
                                <span className={`template-tag cat-badge-${previewTemplate.category}`} style={{ marginLeft: 10, fontWeight: 600 }}>
                                    {CATEGORY_LABELS[previewTemplate.category] || previewTemplate.category}
                                </span>
                            </div>
                            <GlassButton variant="ghost" onClick={() => setPreviewTemplate(null)} style={{ padding: '4px 6px' }}>
                                <X size={18} />
                            </GlassButton>
                        </div>
                        <div className="mp-preview-iframe-wrap">
                            <iframe
                                src={`/api/marketplace/templates/${previewTemplate.template_id}/preview`}
                                title={previewTemplate.name}
                                className="mp-preview-iframe"
                                sandbox="allow-scripts allow-same-origin"
                            />
                        </div>
                        <div className="mp-preview-footer">
                            <GlassButton variant="secondary" onClick={() => setPreviewTemplate(null)}>
                                Close
                            </GlassButton>
                            <GlassButton
                                variant="primary"
                                onClick={() => { setPreviewTemplate(null); handleUse(previewTemplate.template_id); }}
                                disabled={usingId === previewTemplate.template_id}
                            >
                                {usingId === previewTemplate.template_id ? 'Applying...' : 'Use Template'}
                            </GlassButton>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- Publish / Edit Modal ---- */}
            <GlassModal
                isOpen={publishOpen}
                onClose={() => setPublishOpen(false)}
                title={editTemplate ? 'Edit Template' : 'Publish Template'}
                size="lg"
                footer={
                    <>
                        <GlassButton variant="secondary" onClick={() => setPublishOpen(false)}>
                            Cancel
                        </GlassButton>
                        <GlassButton variant="primary" onClick={handlePublishSubmit} disabled={publishLoading}>
                            {publishLoading ? 'Saving...' : editTemplate ? 'Save Changes' : 'Publish'}
                        </GlassButton>
                    </>
                }
            >
                <div className="publish-form">
                    <div className="publish-form-group">
                        <label>Template Name *</label>
                        <GlassInput
                            value={publishForm.name}
                            onChange={(v) => setPublishForm(prev => ({ ...prev, name: v }))}
                            placeholder="e.g. Product Launch Campaign"
                        />
                    </div>

                    <div className="publish-form-group">
                        <label>Category *</label>
                        <GlassSelect
                            value={publishForm.category}
                            onChange={(v) => setPublishForm(prev => ({ ...prev, category: v }))}
                            options={CATEGORY_OPTIONS}
                        />
                    </div>

                    <div className="publish-form-group">
                        <label>Description</label>
                        <textarea
                            value={publishForm.description}
                            onChange={(e) => setPublishForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Describe what this template does and who it's for..."
                            rows={3}
                        />
                    </div>

                    <div className="publish-form-group">
                        <label>Tags (comma-separated)</label>
                        <GlassInput
                            value={publishForm.tags}
                            onChange={(v) => setPublishForm(prev => ({ ...prev, tags: v }))}
                            placeholder="e.g. launch, b2b, product"
                        />
                    </div>

                    <div className="publish-form-group">
                        <label>Template Data (JSON)</label>
                        <textarea
                            value={publishForm.template_data}
                            onChange={(e) => setPublishForm(prev => ({ ...prev, template_data: e.target.value }))}
                            className="code-area"
                            placeholder='{"key": "value"}'
                            rows={6}
                        />
                    </div>

                    <div className="publish-toggle-row">
                        <input
                            type="checkbox"
                            id="is_public"
                            checked={publishForm.is_public}
                            onChange={(e) => setPublishForm(prev => ({ ...prev, is_public: e.target.checked }))}
                        />
                        <label htmlFor="is_public" className="publish-toggle-label">
                            Make this template public (visible to all users)
                        </label>
                    </div>
                </div>
            </GlassModal>

            {/* ---- Toasts ---- */}
            {toasts.map(t => (
                <div key={t.id} className={`mp-toast ${t.type}`}>
                    {t.message}
                </div>
            ))}

            {/* ---- Landing Page Use Modal ---- */}
            {lpModal && (
                <div
                    className="mp-preview-overlay"
                    onClick={e => { if (e.target === e.currentTarget) setLpModal(null); }}
                >
                    <div className="mp-preview-modal lp-use-modal">
                        <div className="mp-preview-header">
                            <div>
                                <span className="mp-preview-title">{lpModal.templateName}</span>
                                <span className="template-tag cat-badge-landing_page" style={{ marginLeft: 10, fontSize: '0.75rem' }}>Landing Page</span>
                            </div>
                            <button className="mp-preview-close" onClick={() => setLpModal(null)}>✕</button>
                        </div>
                        <div className="lp-use-modal-body">
                            <div className="lp-use-field">
                                <label className="lp-use-label">Page Title</label>
                                <input
                                    className="lp-use-input"
                                    type="text"
                                    value={lpTitle}
                                    onChange={e => setLpTitle(e.target.value)}
                                    placeholder="Enter a title for your landing page"
                                    autoFocus
                                />
                            </div>
                            {lpBrandGuides.length > 0 && (
                                <div className="lp-use-field">
                                    <label className="lp-use-label">Brand Guide <span className="lp-use-optional">(optional — applies your branding)</span></label>
                                    <select
                                        className="lp-use-select"
                                        value={lpBrandGuideId}
                                        onChange={e => setLpBrandGuideId(e.target.value)}
                                    >
                                        <option value="">No brand guide (use defaults)</option>
                                        {lpBrandGuides.map(bg => (
                                            <option key={bg.brand_guide_id} value={bg.brand_guide_id}>
                                                {bg.brand_name || bg.brand_guide_name || bg.brand_guide_id}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="lp-use-field">
                                <label className="lp-use-label">Project</label>
                                <div className="lp-use-project-badge">
                                    {selectedProjectId ? `Project ID: ${selectedProjectId.slice(0, 8)}…` : 'No project selected'}
                                </div>
                            </div>
                            <p className="lp-use-hint">
                                This will generate a landing page using the <strong>{lpModal.templateType.replace(/_/g, ' ')}</strong> template and open it in the visual editor. Generation takes 10–20 seconds.
                            </p>
                        </div>
                        <div className="mp-preview-footer">
                            <button className="mp-btn-secondary" onClick={() => setLpModal(null)}>Cancel</button>
                            <button
                                className="mp-btn-primary"
                                onClick={handleLpApply}
                                disabled={lpApplying || !lpTitle.trim() || !selectedProjectId}
                            >
                                {lpApplying ? 'Creating…' : 'Create Landing Page →'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MarketplaceModule;
